import { sortBy } from 'lodash';
import { fromPairs, keyBy } from 'lodash/fp';
import { toJS } from 'mobx';
import { createContext, ReactElement, ReactNode, useEffect, useState } from 'react';
import { v4 } from 'uuid';
import { outerJoinOn } from '../../../../../helpers/array.helpers';
import { buildBounds, getLatLngsForGeojson } from '../../../../../helpers/geometry/polygon.helpers';
import { ACDLError, InputStatus } from '../../../../../models/error.model';
import { ISegmentation } from '../../../annotations.interface';
import { IQuestionModel } from '../../../question.model';
import { VerticalMenuItem } from '../components/verticalMenu/VerticalMenu';
import { useSegmentationsApiService } from './segmentationsApi.service';

interface IImageSegmentationsContext {
  segmentations: Record<string, ISegmentation[]>;
  answeredQuestions: Record<string, IQuestionModel[]>;
  projects: { id: string; name: string }[];
  selectedProjects: string[];
  openedProjects: string[];
  isLoaded: boolean;
  activeTab?: VerticalMenuItem;
  loadAsync(imageId: string): Promise<void>;
  toggleTab(menuItem: VerticalMenuItem | undefined): void;
  setSelectedProjects(projectIds: string[]): void;
  setOpenedProjects(projectIds: string[]): void;
}

const useImageSegmentationsContext = (): IImageSegmentationsContext => {
  const api = useSegmentationsApiService();
  const [segmentations, setSegmentations] = useState<Record<string, ISegmentation[]>>({});
  const [answeredQuestions, setAnsweredQuestions] = useState<Record<string, IQuestionModel[]>>({});
  const [projects, setProjects] = useState<{ id: string; name: string }[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [openedProjects, setOpenedProjects] = useState<string[]>([]);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<VerticalMenuItem | undefined>(undefined);

  const clear = (): void => {
    setSegmentations({});
    setProjects([]);
    setSelectedProjects([]);
    setOpenedProjects([]);
    setIsLoaded(false);
  };

  const loadAsync = async (imageId: string): Promise<void> => {
    if (projects || segmentations) {
      clear();
    }

    const result = await api.peekImageAnnotations(imageId);
    if (result instanceof ACDLError) {
      return;
    }

    const annotationTypes = keyBy('id', result.annotationTypes);

    setSegmentations(
      fromPairs(
        result.annotations
          .map(({ projectId, annotations }) => [
            projectId,
            annotations.segmentations.map((s) => {
              const feature = {
                ...s.feature,
                id: v4(),
                color: annotationTypes[s.feature.properties?.annotationTypeId].color,
                featureType: s.feature.properties?.featureType,
              };

              const latlngs = getLatLngsForGeojson(feature);

              return {
                feature,
                latlngs,
                questions: result.questions
                  .filter((q) => q.scopes.includes(s.feature.properties?.annotationTypeId))
                  .map((question): IQuestionModel => {
                    const q = s.questions.find((sq) => sq.id === question.id);
                    const answers = toJS(q ? outerJoinOn('id', 'id', question.answers, q.answers) : []);
                    const model = { ...question, ...q, answers, status: InputStatus.empty() };
                    return model;
                  }),
                bbox: buildBounds(latlngs),
                priority: 0,
              } as ISegmentation;
            }),
          ])
          .concat(result.emptyProjects.map((ep) => [ep.id, []]))
      )
    );
    setAnsweredQuestions(
      fromPairs(
        result.annotations
          .map(({ projectId, annotations }) => [
            projectId,
            sortBy(annotations.questions, 'order')
              .map((aq): IQuestionModel | undefined => {
                const question = result.questions.find((q) => q.id === aq.id);
                if (!question) {
                  return undefined;
                }
                return {
                  ...question,
                  status: InputStatus.empty(),
                  answer: aq.answer,
                  answers: question.answers.map((a) => ({
                    ...a,
                    selected: aq.answers.findIndex((answer) => answer.id === a.id) !== -1,
                  })),
                };
              })
              .filter((x) => x),
          ])
          .concat(result.emptyProjects.map((ep) => [ep.id, []]))
      )
    );
    setProjects(result.projects.concat(result.emptyProjects));
    setIsLoaded(true);
  };

  const toggleTab = (menuItem: VerticalMenuItem | undefined): void => {
    setActiveTab(activeTab === menuItem ? undefined : menuItem);
  };

  return {
    segmentations,
    answeredQuestions,
    projects,
    selectedProjects,
    openedProjects,
    isLoaded,
    activeTab,
    loadAsync,
    toggleTab,
    setSelectedProjects,
    setOpenedProjects,
  };
};

export const ImageSegmentations = createContext<IImageSegmentationsContext>(undefined);

interface IImageSegmentationsContextProviderProps {
  imageId?: string;
  children: ReactNode;
}

export const ImageSegmentationsContextProvider = ({ imageId, children }: IImageSegmentationsContextProviderProps): ReactElement => {
  const ctx = useImageSegmentationsContext();

  useEffect(() => {
    if (imageId) {
      void ctx.loadAsync(imageId);
    }
  }, [imageId]);

  return <ImageSegmentations.Provider value={ctx}>{children}</ImageSegmentations.Provider>;
};
